import { useQuery } from "@tanstack/react-query"
import { FilePlus2, Package } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError, EstadoVacio } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

import { EditorRecibo } from "./EditorRecibo"
import type { Asignacion } from "./tipos"

/**
 * Las cargas que SETIECA le asignó al intermediario desde el 2.0 (25/10): el expediente, el
 * BL y la mercancía ya vienen puestos; él hace el recibo de cada flete con la empresa, el
 * vehículo, el chofer y el monto. Al emitirlo, el reporte llega solo al 2.0.
 */
export function CargasAsignadas() {
  const [abierta, setAbierta] = useState<Asignacion | null>(null)
  const cargas = useQuery({
    queryKey: ["transporte", "asignaciones"],
    queryFn: () => api.get<Asignacion[]>("/api/v1/transporte/asignaciones"),
    refetchInterval: 60_000,
  })

  if (cargas.isPending) return <EstadoCarga etiqueta="Cargando cargas asignadas" />
  if (cargas.isError) {
    return (
      <EstadoError
        titulo="No se pudieron cargar las cargas"
        error={cargas.error}
        onReintentar={() => cargas.refetch()}
      />
    )
  }
  if (cargas.data.length === 0) {
    return (
      <EstadoVacio
        titulo="Sin cargas asignadas"
        texto="Cuando SETIECA le asigne el flete de un expediente, aparece aquí con su BL y su mercancía."
      />
    )
  }
  return (
    <>
      <ul className="grid gap-3 lg:grid-cols-2">
        {cargas.data.map((a) => {
          const emitidos = (a.recibos.EMITIDO ?? 0) + (a.recibos.RECIBIDO ?? 0)
          return (
            <li key={a.id} className="tarjeta space-y-2 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    Asignada el {comoFechaHora(a.asignado_en)}
                  </p>
                  <h3 className="flex items-center gap-2 font-semibold">
                    <Package size={16} aria-hidden={true} /> Expediente {a.numero}
                  </h3>
                </div>
                {emitidos > 0 ? (
                  <Insignia tono="exito">
                    {emitidos} recibo{emitidos === 1 ? "" : "s"} emitido{emitidos === 1 ? "" : "s"}
                  </Insignia>
                ) : (
                  <Insignia tono="aviso">Por reportar</Insignia>
                )}
              </div>
              <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
                <Dato t="BL" v={a.carga.bl} />
                <Dato t="Buque" v={a.carga.buque} />
                <Dato t="ETA" v={a.carga.eta ? comoFecha(a.carga.eta) : null} />
                <Dato
                  t="Carga"
                  v={[a.carga.cantidad_carga && Number(a.carga.cantidad_carga), a.carga.tipo_carga]
                    .filter(Boolean)
                    .join(" × ")}
                />
                <div className="sm:col-span-2">
                  <Dato t="Mercancía" v={a.carga.descripcion_mercancia ?? a.carga.tipo_mercancia} />
                </div>
              </dl>
              {a.fletes.length > 0 && (
                <ul className="text-sm" style={{ color: "var(--fg-muted)" }}>
                  {a.fletes.map((f, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: renglones del 2.0, sin id propio
                    <li key={i}>
                      {Number(f.cantidad) || 1} × {f.concepto}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end">
                <Boton
                  variante={emitidos ? "secundario" : "primario"}
                  onClick={() => setAbierta(a)}
                >
                  <FilePlus2 size={16} aria-hidden={true} /> Hacer recibo de esta carga
                </Boton>
              </div>
            </li>
          )
        })}
      </ul>
      {abierta && (
        <EditorRecibo recibo={null} asignacion={abierta} onCerrar={() => setAbierta(null)} />
      )}
    </>
  )
}

function Dato({ t, v }: { t: string; v: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {t}
      </dt>
      <dd>{v || "—"}</dd>
    </div>
  )
}
